import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { User, Globe, Shield, Sparkles, KeyRound, Lock, Eye, EyeOff, LogOut, MonitorSmartphone, Monitor, UserCircle, ShieldCheck, Cpu, BellRing, MessagesSquare } from "lucide-react";
import { ModelRoutesCard } from "../components/ModelRoutesCard";
import { NotulenFormatCard } from "../components/NotulenFormatCard";
import { SmartRoutingToggle } from "../components/ModelRoutingCard";
import { PushSettingsCard } from "../components/PushSettingsCard";
import { SoundSettingsCard } from "../components/SoundToggle";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { AvatarUploader } from "../components/AvatarUploader";
import { DataPrivacyCard } from "../components/DataPrivacyCard";
import { ConsentCenterCard } from "../components/ConsentCenterCard";
import { confirmAsync } from "../components/ConfirmDialog";

const LANGS = [{ v: "id", l: "Bahasa Indonesia" }, { v: "en", l: "English" }];
const TABS = [
  { id: "account", label: "Akun", Icon: UserCircle },
  { id: "security", label: "Keamanan", Icon: ShieldCheck },
  { id: "models", label: "Pengaturan Agen", Icon: Cpu },
  { id: "notifications", label: "Notifikasi & Suara", Icon: BellRing },
  { id: "conversation", label: "Percakapan", Icon: MessagesSquare },
];

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== pw;
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error("Ulangi password tidak sama"); return; }
    setBusy(true);
    try { const r = await api.post("/auth/change-password", { current_password: cur, new_password: pw }); if (r.data?.access_token) setAuthToken(r.data.access_token); toast.success("Password berhasil diubah"); setCur(""); setPw(""); setConfirm(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengubah password"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 aivora-card p-6" data-testid="change-password-card">
      <p className="flex items-center gap-2 font-semibold text-slate-900"><KeyRound size={16} className="text-[#2F6BFF]" /> Ubah Password</p>
      <p className="mt-1 text-xs text-slate-500">Masukkan password lama untuk verifikasi, lalu password baru (minimal 6 karakter).</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="relative"><Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-dark pl-10" type={show ? "text" : "password"} placeholder="Password lama" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="pw-current" /></div>
        <div className="relative"><KeyRound size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-dark pl-10" type={show ? "text" : "password"} placeholder="Password baru" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} data-testid="pw-new" /></div>
        <div className="relative"><KeyRound size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`input-dark pl-10 ${mismatch ? "border-[#EF4444]" : ""}`} type={show ? "text" : "password"} placeholder="Ulangi password baru" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="pw-confirm" /></div>
      </div>
      {mismatch && <p className="mt-1.5 text-xs font-medium text-[#EF4444]" data-testid="pw-confirm-error">Password tidak sama.</p>}
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-grad rounded-xl px-5 py-2.5 text-sm disabled:opacity-50" disabled={busy || mismatch} data-testid="pw-submit">{busy ? "..." : "Simpan Password"}</button>
        <button type="button" onClick={() => setShow(!show)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-testid="pw-toggle-show">{show ? <EyeOff size={14} /> : <Eye size={14} />} {show ? "Sembunyikan" : "Tampilkan"}</button>
      </div>
    </form>
  );
}


function ActiveSessionsCard() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/auth/sessions").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const revoke = async (s) => {
    if (!await confirmAsync(`Cabut sesi "${s.device}"? Perangkat itu akan diminta login ulang.`)) return;
    setBusy(s.id);
    try { await api.delete(`/auth/sessions/${s.id}`); toast.success("Sesi dicabut"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencabut sesi"); } finally { setBusy(""); }
  };
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
  return (
    <div className="mt-4 aivora-card p-6" data-testid="sessions-card">
      <p className="flex items-center gap-2 font-semibold text-slate-900"><MonitorSmartphone size={16} className="text-[#2F6BFF]" /> Perangkat & Sesi Aktif</p>
      <p className="mt-1 text-xs text-slate-500">Daftar perangkat yang sedang login ke akun Anda. Cabut sesi yang tidak Anda kenali.</p>
      <div className="mt-4 space-y-2">
        {rows === null && <p className="text-xs text-slate-400" data-testid="sessions-loading">Memuat…</p>}
        {rows && rows.length === 0 && <p className="text-xs text-slate-400">Tidak ada sesi aktif.</p>}
        {rows && rows.map((s) => (
          <div key={s.id} className="flex items-center gap-3 rounded-xl border border-[#E7ECF3] bg-white px-4 py-3" data-testid={`session-${s.id}`}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-[#2F6BFF]"><Monitor size={16} /></div>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-800" data-no-i18n>{s.device}{s.current && <span className="rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold text-[#059669]" data-no-i18n>Perangkat ini</span>}</p>
              <p className="truncate text-[11px] text-slate-400" data-no-i18n>{[s.location, s.ip].filter(Boolean).join(" · ") || "Lokasi tidak diketahui"} · aktif {fmt(s.last_seen_at)}</p>
            </div>
            {!s.current && !s.legacy && <button onClick={() => revoke(s)} disabled={busy === s.id} className="shrink-0 rounded-lg border border-[#EF4444]/30 px-3 py-1.5 text-xs font-semibold text-[#EF4444] transition hover:bg-[#EF4444]/10 disabled:opacity-50" data-testid={`session-revoke-${s.id}`}>{busy === s.id ? "..." : "Cabut"}</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function LogoutAllCard() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!await confirmAsync("Keluar dari semua perangkat yang sedang login? Perangkat ini akan tetap masuk, sesi lain akan diminta login ulang.")) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/logout-all");
      if (r.data?.access_token) setAuthToken(r.data.access_token);
      toast.success("Semua sesi lain telah dikeluarkan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencabut sesi"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 aivora-card p-6" data-testid="logout-all-card">
      <p className="flex items-center gap-2 font-semibold text-slate-900"><LogOut size={16} className="text-[#2F6BFF]" /> Keluar dari Semua Perangkat</p>
      <p className="mt-1 text-xs text-slate-500">Mencabut semua sesi login aktif di perangkat lain demi keamanan akun. Perangkat ini tetap login.</p>
      <button onClick={run} disabled={busy} className="mt-4 rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/5 px-5 py-2.5 text-sm font-semibold text-[#EF4444] transition hover:bg-[#EF4444]/10 disabled:opacity-50" data-testid="logout-all-btn">{busy ? "Memproses..." : "Keluarkan Semua Sesi Lain"}</button>
    </div>
  );
}


export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, setLang } = useI18n();
  const s = user?.settings || {};
  const [name, setName] = useState(user?.name || "");
  const [appLang, setAppLang] = useState(s.app_language || "id");
  const [convLang, setConvLang] = useState(s.conversation_language || "id");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(() => (TABS.some((x) => x.id === window.location.hash.slice(1)) ? window.location.hash.slice(1) : "account"));
  useEffect(() => { window.history.replaceState(null, "", `#${tab}`); }, [tab]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/onboard", { name, app_language: appLang, conversation_language: convLang, timezone: s.timezone || "Asia/Jakarta" });
      setUser(r.data); setLang(appLang); toast.success("Tersimpan");
    } catch (e) { toast.error("Gagal"); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl p-5 sm:p-8 lg:p-10 fade-up" data-testid="profile-page">
      <h1 className="text-3xl font-extrabold text-slate-900">{t("nav.profile")}</h1>

      <div className="mt-5 flex gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1" role="tablist" data-testid="profile-tabs">
        {TABS.map((tb) => (
          <button key={tb.id} role="tab" aria-selected={tab === tb.id} onClick={() => setTab(tb.id)} data-testid={`profile-tab-${tb.id}`}
            className={`flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition sm:text-sm ${tab === tb.id ? "bg-[#2F6BFF] text-white shadow" : "text-slate-600 hover:bg-slate-100"}`}>
            <tb.Icon size={14} /> {tb.label}
          </button>
        ))}
      </div>

      {tab === "account" && <>
        <div className="mt-4 aivora-card p-6" data-testid="profile-account-card">
          <div className="flex items-center gap-4">
            <AvatarUploader />
            <div>
              <p className="text-lg font-bold text-slate-900">{user?.name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
              {user?.role === "admin" && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#7C3AED]/20 px-2 py-0.5 text-xs text-[#a78bfa]"><Shield size={11} /> Admin</span>}
            </div>
            <div className="ml-auto flex items-center gap-1.5 rounded-full border border-[rgba(0,209,255,.25)] bg-slate-50 px-3 py-1.5 text-sm"><Sparkles size={14} className="text-[#2F6BFF]" /> <span className="font-semibold text-slate-900">{user?.trial ? (user?.usable_credits ?? user?.credits) : user?.credits}</span></div>
          </div>
          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><User size={13} /> Nama</label>
              <input className="input-dark" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><Globe size={13} /> Bahasa Aplikasi</label>
                <select className="input-dark" value={appLang} onChange={(e) => setAppLang(e.target.value)} data-testid="profile-applang">{LANGS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}</select>
              </div>
              <div>
                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><Globe size={13} /> Bahasa Percakapan</label>
                <select className="input-dark" value={convLang} onChange={(e) => setConvLang(e.target.value)} data-testid="profile-convlang">{LANGS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}</select>
              </div>
            </div>
            <button onClick={save} disabled={busy} className="btn-grad rounded-xl px-6 py-3 text-sm" data-testid="profile-save">{busy ? "..." : t("common.save")}</button>
          </div>
        </div>
        <div className="mt-4 aivora-card p-6 text-sm text-slate-500">
          <p className="font-semibold text-slate-900">Privasi & Data</p>
          <p className="mt-1">Data percakapan & memori Anda hanya digunakan untuk menjalankan layanan. Foto referensi persona hanya dipakai untuk penampilan visual. Anda dapat menghapus persona, memori, dan percakapan kapan saja.</p>
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="profile-legal-links">
            <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-[#2F6BFF] hover:underline" data-testid="profile-terms-link">Ketentuan Layanan</a>
            <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-[#2F6BFF] hover:underline" data-testid="profile-privacy-link">Kebijakan Privasi</a>
          </p>
        </div>
        <ConsentCenterCard />
        <DataPrivacyCard />
      </>}

      {tab === "security" && <><ChangePasswordCard /><ActiveSessionsCard /><LogoutAllCard /></>}

      {tab === "models" && <>
        {user?.role === "admin" && <SmartRoutingToggle user={user} onSaved={() => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {})} />}
        <ModelRoutesCard />
      </>}

      {tab === "notifications" && <><SoundSettingsCard /><PushSettingsCard /></>}

      {tab === "conversation" && <>
        {user?.role === "admin" && <NotulenFormatCard user={user} onSaved={() => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {})} />}
      </>}
    </div>
  );
}
